import dayjs from "dayjs";

import { toProv } from "../../components/Analytics/PriceReports/provinceUtils";
import { normalizeProductIdFromBreakdownRow } from "../../services/beefAiPricing";
import { cadCurrencyFormat } from "./constants";
import { buildGpsDisplayFromExifResolution } from "./priceHistoryGpsCoordinates";

function formatWeightLabel(row) {
  const unit = typeof row?.unit === "string" ? row.unit.trim() : "";
  const unitQuantity = row?.unit_quantity;
  const hasNumericQuantity =
    typeof unitQuantity === "number"
      ? Number.isFinite(unitQuantity)
      : typeof unitQuantity === "string" && unitQuantity.trim() !== "" && !Number.isNaN(Number(unitQuantity));

  if (hasNumericQuantity && unit) {
    return `${Number(unitQuantity)} ${unit}`;
  }
  if (unit) return unit;
  return "";
}

function toNumberOrNull(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * @param {any[]} rows Normalized breakdown rows from `fetchPriceHistoryBreakdown`.
 * @returns {Array<{
 *   id: string,
 *   recordAt: string,
 *   product: string,
 *   retailer: string,
 *   province: string,
 *   unitPrice: string,
 *   unitPriceValue: number | null,
 *   price: string,
 *   weight: string,
 *   verified: boolean,
 *   images: string[],
 *   revisionStatus: string | null,
 *   revisionMetadata: object | null,
 *   productId: string,
 *   recordAtValue: import("dayjs").Dayjs | null,
 *   retailerName: string,
 *   retailerRefId: string,
 *   provinceCode: string,
 *   provinceStored: string,
 *   priceValue: number | null,
 *   unit: string,
 *   unitQuantityValue: number | null,
 *   gpsCoordinatesLabel: string | null,
 *   googleMapsUrl: string | null,
 * }>}
 */
export function mapPriceHistoryBreakdownRowsToDisplayItems(rows) {
  return (rows || []).map((row, index) => {
    const gpsDisplay = buildGpsDisplayFromExifResolution(row?.exifResolutionGps);

    return {
      id: row?.id || `${row?.date_created || "row"}-${index}`,
      recordAt: row?.record_at ? dayjs(row.record_at).format("YYYY-MM-DD") : "—",
      recordAtValue: row?.record_at ? dayjs(row.record_at) : null,
      product: row?.product_name || row?.raw_product_name || "—",
      productId: normalizeProductIdFromBreakdownRow(row),
      retailer: row?.retailer || "—",
      retailerName: typeof row?.retailer === "string" ? row.retailer.trim() : "",
      retailerRefId:
        typeof row?.retailer_ref_id === "string" ? row.retailer_ref_id.trim() : "",
      province: row?.province || "—",
      provinceCode: toProv(row?.province || ""),
      provinceStored: typeof row?.province === "string" ? row.province.trim() : "",
      unitPrice:
        typeof row?.unit_price === "number"
          ? (() => {
              const unit = typeof row?.unit === "string" ? row.unit.trim() : "";
              const formattedUnitPrice = cadCurrencyFormat.format(row.unit_price);
              return unit ? `${formattedUnitPrice} per ${unit}` : formattedUnitPrice;
            })()
          : "—",
      unitPriceValue: toNumberOrNull(row?.unit_price),
      price: typeof row?.price === "number" ? cadCurrencyFormat.format(row.price) : "—",
      priceValue: toNumberOrNull(row?.price),
      unit: typeof row?.unit === "string" ? row.unit.trim() : "",
      unitQuantityValue: toNumberOrNull(row?.unit_quantity),
      weight: formatWeightLabel(row) || "—",
      verified: Boolean(row?.human_verified),
      voided: Boolean(row?.is_void),
      images: Array.isArray(row?.images) ? row.images : [],
      revisionStatus:
        typeof row?.revision_status === "string" && row.revision_status.trim()
          ? row.revision_status
          : null,
      revisionMetadata:
        row?.revision_metadata && typeof row.revision_metadata === "object"
          ? row.revision_metadata
          : null,
      gpsCoordinatesLabel: gpsDisplay?.gpsCoordinatesLabel ?? null,
      googleMapsUrl: gpsDisplay?.googleMapsUrl ?? null,
    };
  });
}

/**
 * @param {{ month?: string, productName?: string, province?: string, retailer?: string } | null | undefined} aggregateRow
 */
export function formatPriceHistoryBreakdownAggregateContext(aggregateRow) {
  if (!aggregateRow) return "";
  return [
    aggregateRow.month ? `Month: ${aggregateRow.month}` : null,
    aggregateRow.productName ? `Product: ${aggregateRow.productName}` : null,
    aggregateRow.province ? `Province: ${aggregateRow.province}` : null,
    aggregateRow.retailer ? `Retailer: ${aggregateRow.retailer}` : null,
  ]
    .filter(Boolean)
    .join(" | ");
}

/**
 * @param {{ productName?: string } | null | undefined} aggregateRow
 */
export function formatGalleryBreakdownDialogTitle(aggregateRow) {
  const productName = aggregateRow?.productName?.trim();
  return `${productName || "Product"} Gallery`;
}
